import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Calendar, Users, CheckCircle2, Clock, Wallet, Search, Settings, ClipboardList } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatError } from "@/lib/api";
import { ESTADO_RESERVA } from "@/lib/estados";
import { fecha } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { esAdmin } from "@/lib/roles";
import { assetUrl, useBrand } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Pantallas de configuración, agrupadas para que la cabecera quepa.
const AJUSTES = [
  ["/admin/empresa", "Empresa", "admin-company-btn"],
  ["/admin/cobros", "Cobros", "admin-payments-btn"],
  ["/admin/contrato", "Contrato", "admin-contract-btn"],
  ["/admin/textos-legales", "Privacidad y aviso legal", "admin-legal-btn"],
  ["/admin/privacidad", "Protección de datos", "admin-privacy-btn"],
  ["/admin/registro-viajeros/ajustes", "Registro de viajeros", "admin-registry-settings-btn"],
  ["/admin/usuarios", "Usuarios", "admin-users-btn"],
  ["/admin/asistente", "Asistente de WhatsApp", "admin-assistant-btn"],
  ["/admin/ajustes-reserva", "Reserva online", "admin-booking-settings-btn"],
  ["/admin/temporadas", "Temporadas", "admin-seasons-btn"],
  ["/admin/productos", "Productos", "admin-products-btn"],
  ["/admin/checklist", "Checklist de devolución", "admin-checklist-btn"],
];

function StatCard({ icon, label, value, color = "rgb(var(--marca-rgb))" }) {
  return (
    <div className="bg-white border border-[#DCD8D0] rounded-xl p-5 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <span className="text-overline">{label}</span>
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `color-mix(in srgb, ${color} 8%, transparent)`, color }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl font-bold tabular-nums text-[#1A1A1A]">
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  // El operador no ve flota, finanzas ni configuración (ni sus avisos): el backend se lo negaría igualmente.
  const admin = esAdmin(user);
  const brand = useBrand();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [faltan, setFaltan] = useState([]);
  const [cobros, setCobros] = useState(null);
  // Comunicaciones del registro de viajeros que piden atención (insignia del botón «Registro»).
  const [registro, setRegistro] = useState(0);
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState("");
  // Las retenciones online caducadas o abandonadas se ocultan salvo que se pidan.
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/reservations", { params: showAll ? { all: 1 } : {} }),
      ]);
      setStats(s.data);
      setReservations(r.data);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  useEffect(() => {
    api.get("/admin/company").then(({ data }) => {
      const f = [];
      if (!data.legal_name) f.push("la razón social");
      if (!data.tax_id) f.push("el NIF");
      if (!data.email && !data.notify_email) f.push("un correo (sin él no te llegan avisos ni mensajes de clientes)");
      setFaltan(f);
    }).catch(() => {});
    api.get("/admin/payments/status").then(({ data }) => setCobros(data)).catch(() => {});
    // Falla en silencio: el registro puede no usarse.
    api.get("/admin/registry/summary").then(({ data }) => setRegistro(Number(data?.attention) || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const filtered = reservations.filter(
    (r) =>
      r.client_name.toLowerCase().includes(search.toLowerCase()) ||
      r.code.toLowerCase().includes(search.toLowerCase()),
  );

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0] sticky top-0 z-30">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="flex items-center gap-3 min-w-0">
            {brand?.logo_url
              ? <img src={assetUrl(brand.logo_url)} alt="" className="h-9 w-auto" data-testid="admin-logo" />
              : (
                <div className="h-9 w-9 shrink-0 rounded-full bg-marca text-white flex items-center justify-center font-display font-black">
                  {(brand?.brand_name || "·").charAt(0).toUpperCase()}
                </div>
              )}
            <div className="min-w-0">
              <div className="font-display font-bold text-[#1A1A1A] truncate" data-testid="admin-brand">{brand?.brand_name || ""}</div>
              <div className="text-xs text-[#8A8A8A] truncate">{user?.email}</div>
            </div>
          </div>
          <nav className="flex flex-wrap items-center justify-end gap-1">
            <Button variant="ghost" onClick={() => navigate("/admin/calendario")} data-testid="admin-calendar-btn">
              Calendario
            </Button>
            {admin && (
              <Button variant="ghost" onClick={() => navigate("/admin/flota")} data-testid="admin-fleet-btn">
                Flota
              </Button>
            )}
            {admin && (
              <Button variant="ghost" onClick={() => navigate("/admin/finanzas")} data-testid="admin-finance-btn">
                Finanzas
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate("/admin/registro-viajeros")} data-testid="admin-registry-btn"
              aria-label={registro > 0 ? `Registro de viajeros: ${registro} ${registro === 1 ? "pide" : "piden"} atención` : "Registro de viajeros"}>
              <ClipboardList className="h-4 w-4 mr-2" /> Registro
              {registro > 0 && <Badge className="ml-2 bg-acento text-white border-0" data-testid="admin-registry-badge">{registro}</Badge>}
            </Button>
            {admin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" data-testid="admin-settings-menu">
                    <Settings className="h-4 w-4 mr-2" /> Configuración
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {AJUSTES.map(([ruta, texto, id]) => (
                    <DropdownMenuItem key={ruta} onSelect={() => navigate(ruta)} data-testid={id}>
                      {texto}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" onClick={() => navigate("/admin/usuarios")} data-testid="admin-account-btn">Mi cuenta</Button>
            )}
            {brand?.website && (
              <Button variant="ghost" onClick={() => window.open(brand.website, "_blank", "noopener")} data-testid="admin-web-btn">
                Web ↗
              </Button>
            )}
            <Button
              onClick={() => navigate("/admin/reservations/new")}
              className="bg-acento hover:bg-acento-hover text-white"
              data-testid="admin-new-reservation-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Nueva reserva
            </Button>
            <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </nav>
        </div>
      </header>

      <main className="container-x py-8 fade-in">
        {admin && cobros && !cobros.ready && cobros.mode !== "demo" && (
          <Link to="/admin/cobros" className="mb-6 block rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="admin-payments-warning">
            Los cobros online no están activos: la reserva online no puede vender. Revisa la pantalla de Cobros.
          </Link>
        )}
        {admin && faltan.length > 0 && (
          <Link to="/admin/empresa" className="mb-6 block rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="admin-profile-warning">
            Completa el perfil de tu empresa antes de alquilar: falta {faltan.join(", ")}. Salen en el contrato y en los correos.
          </Link>
        )}
        <div className="mb-8">
          <span className="text-overline">Panel</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mt-2">
            Resumen de la flota
          </h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={<Users className="h-4 w-4" />} label="Total reservas" value={stats?.total ?? "·"} />
          <StatCard icon={<Clock className="h-4 w-4" />} label="Pendientes" value={stats?.pending ?? "·"} color="#E89B33" />
          <StatCard icon={<Calendar className="h-4 w-4" />} label="En curso" value={stats?.active ?? "·"} />
          <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Completadas" value={stats?.completed ?? "·"} color="#4A7C59" />
          <StatCard icon={<Wallet className="h-4 w-4" />} label="Fianzas activas" value={stats?.deposits_held ?? "·"} color="rgb(var(--acento-rgb))" />
        </div>

        <div className="bg-white border border-[#DCD8D0] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#DCD8D0] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-semibold text-[#1A1A1A]">
              Reservas
            </h2>
            <label className="flex items-center gap-2 text-sm text-[#5C5C5C]">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="admin-show-all" />
              Ver también caducadas y canceladas online
            </label>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8A8A]" />
              <Input
                placeholder="Buscar por nombre o código"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#F7F5F0] border-[#DCD8D0]"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-[#8A8A8A]">
              {reservations.length === 0
                ? "Aún no hay reservas. Crea la primera para empezar."
                : "Sin resultados."}
            </div>
          ) : (
            <ul className="divide-y divide-[#DCD8D0]">
              {filtered.map((r) => {
                const st = ESTADO_RESERVA[r.status] || ESTADO_RESERVA.pending;
                return (
                  <li key={r.id}>
                    <Link
                      to={`/admin/reservations/${r.id}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-[#F7F5F0] transition-colors"
                      data-testid={`admin-reservation-row-${r.code}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="font-mono text-sm font-semibold text-marca bg-[#EAE7E0] px-2.5 py-1 rounded-md">
                          {r.code}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-[#1A1A1A] truncate">
                            {r.client_name}
                          </div>
                          <div className="text-xs text-[#8A8A8A]">
                            {fecha(r.pickup_date)} · {r.pickup_time} → {fecha(r.return_date)} · {r.return_time}{r.vehicle?.name ? ` · ${r.vehicle.name}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.payment_status === "paid" && (
                          <Badge className="bg-[#4A7C59]/15 text-[#2F5538] hover:bg-[#4A7C59]/15 border-0">
                            Fianza pagada
                          </Badge>
                        )}
                        {r.payment_status === "refunded" && (
                          <Badge className="bg-[#5C5C5C]/15 text-[#5C5C5C] hover:bg-[#5C5C5C]/15 border-0">
                            Fianza devuelta
                          </Badge>
                        )}
                        <Badge className={`${st.cls} hover:${st.cls} border-0`}>
                          {st.label}
                        </Badge>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
